import { useEffect, useRef, useState, useCallback } from 'react'
import { useLocation, useSearchParams } from 'react-router'
import { useSelector } from 'react-redux'

import * as sessionUserSelector from 'selectors/sessionUser'

import { stringifyQuery, parsedQuery } from 'utils/strings'
// usage:
//
// const [count, setCount] = useState(0)
// const prevCount = usePrevious(count)
// return <h1>Now: {count}, before: {prevCount}</h1>
export function usePrevious (value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })

  return ref.current
}

// Effect triggers on component mount
//
// function Hello () {
//   useOnMount(() => {
//     // use any App props or result of other hooks
//     ...
//   })
//
//   return <div>hello</div>
// }
export function useOnMount (fn) {
  const [res] = useState(fn)
  return res
}

// Returns true for the first render
//
// const [active, setActive] = useState(false)
// const firstRender = useFirstRender()
// useEffect(() => {
//   if (!firstRender) {
//     ...
//   }
// }, [active])
export function useFirstRender () {
  const firstRender = useRef(true)

  useEffect(() => {
    firstRender.current = false
  }, [])

  return firstRender.current
}

// Returns debounced function which will be executed only after "timeout" ms.
//
// const onDateChangeWithDelay = useDebouncedFunc(onDateChange, 700)
export function useDebouncedFunc (func, timeout) {
  const timeoutRef = useRef(null)

  const funcWithDelay = useCallback((...args) => {
    // Clear the previous timeout
    clearTimeout(timeoutRef.current)

    // Set a new timeout to call the function
    timeoutRef.current = setTimeout(() => {
      func(...args)
    }, timeout)
  }, [func, timeout])

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => clearTimeout(timeoutRef.current)
  }, [])

  return funcWithDelay
}

// scroll page to element with anchorElementId id
// also works asynchronousely after updating data. Toggling of 'skip' parameter triggers re-check of anchorElementId
//
// const anchorElementId = useAnchorScroll({ skip: isFetching })
export function useAnchorScroll ({ skip }) {
  const anchorElementId = useLocation().hash?.replace('#', '')

  useEffect(() => {
    if (skip) {
      return
    }

    const elementToScroll = anchorElementId ? document.getElementById(anchorElementId) : null
    if (elementToScroll) {
      setTimeout(() => {
        elementToScroll.scrollIntoView()
      }, 1)
    }
  }, [anchorElementId, skip])

  return anchorElementId
}

// if URL hash #comments-274 then useHashIdByKey('comments') returns 274
export function useHashIdByKey (hashKey) {
  const location = useLocation()
  const hashKeyValue = location.hash?.replace('#', '')?.split('-')
  return hashKeyValue[0] === hashKey ? hashKeyValue[1] : null
}

// check user's feature flag
//
// import { useCheckFeature } from 'utils/hooks'
// const showJustificationOnConfirm = useCheckFeature('RELAY_SOMETHING')
export function useCheckFeature (feature) {
  if (process.env.STORYBOOK) {
    const features = localStorage.getItem('storybook_features') || ''
    if (features.split(',').includes(feature)) {
      return true
    }
    return false
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const features = useSelector(sessionUserSelector.features) || []
  const hasFeature = features.includes(feature)
  return hasFeature
}

export function useLocationQueryParams () {
  const location = useLocation()
  const locationQueryParams = parsedQuery(location.search)

  return [locationQueryParams, location]
}

export function useSetSearchParamsWithQueryString () {
  const [, setSearchParams] = useSearchParams()

  const setQueryParam = (query) => {
    setSearchParams(stringifyQuery(query))
  }

  return setQueryParam
}
